import React, { useEffect } from 'react';

// Privacy contact email — the value already configured in the PandaProp project
// (existing privacy-policy.html and store listing). Not invented.
const PRIVACY_EMAIL = 'info@pandaprop.tech';
const LAST_UPDATED = 'Ağustos 2026';

const Section = ({ title, children }) => (
  <section className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 sm:p-8 mb-6">
    <h2 className="text-xl sm:text-2xl font-bold text-teal-700 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
      {title}
    </h2>
    <div className="text-base text-gray-700 leading-relaxed space-y-3" style={{ fontFamily: 'Inter, sans-serif' }}>
      {children}
    </div>
  </section>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3 text-gray-700">
    <span className="text-teal-500 mt-0.5">•</span>
    <span className="text-base">{children}</span>
  </li>
);

export const PrivacyPage = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'PandaProp Gizlilik Politikası';
    let meta = document.querySelector('meta[name="description"]');
    let created = false;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
      created = true;
    }
    const prevDesc = meta.getAttribute('content');
    meta.setAttribute(
      'content',
      'PandaProp Sakin uygulamasının kişisel verileri nasıl işlediğini, sakladığını ve koruduğunu açıklayan gizlilik politikası.'
    );
    return () => {
      document.title = prevTitle;
      if (created && meta.parentNode) meta.parentNode.removeChild(meta);
      else if (meta && prevDesc != null) meta.setAttribute('content', prevDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 flex flex-col" data-testid="privacy-page">
      {/* Header */}
      <header className="w-full border-b border-gray-200/70 bg-white/60 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src="/assets/pandaprop-logo.png" alt="PandaProp" className="h-11 sm:h-14 w-auto" />
          </a>
          <a href="/" className="text-sm font-semibold text-teal-700 hover:text-teal-800 transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>
            Ana Sayfa
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        <div className="container mx-auto px-4 py-12 sm:py-16">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Gizlilik Politikası
              </h1>
              <p className="text-sm text-gray-500" style={{ fontFamily: 'Inter, sans-serif' }}>Son güncelleme: {LAST_UPDATED}</p>
            </div>

            <Section title="Genel Bakış">
              <p>
                Bu gizlilik politikası, PandaProp Sakin uygulamasının kişisel verilerinizi nasıl işlediğini açıklar.
                PandaProp, apartman ve site sakinlerinin aidat, borç, ödeme, duyuru ve daireleriyle ilgili bilgilere
                erişmesini sağlayan bir platformdur. Uygulamayı kullanarak bu politikada açıklanan uygulamaları kabul
                etmiş olursunuz.
              </p>
            </Section>

            <Section title="Topladığımız Veriler">
              <p>PandaProp Sakin uygulaması yalnızca hizmeti sunmak için gerekli verileri işler:</p>
              <ul className="space-y-2">
                <Bullet><strong>Hesap bilgileri:</strong> Ad-soyad, e-posta adresi, telefon numarası (opsiyonel), kullanıcı adı ve rol (sakin/yönetici).</Bullet>
                <Bullet><strong>Daire ve ilişki bilgileri:</strong> Daire numarası, bina/kat bilgisi ve malik/kiracı ilişkisi.</Bullet>
                <Bullet><strong>Finansal bilgiler:</strong> Aidat, diğer ödemeler, borç ve bakiye durumu ile ödeme/işlem geçmişi. Bu kayıtlar apartman yönetimi tarafından oluşturulur; uygulama üzerinden görüntülenir.</Bullet>
                <Bullet><strong>Kimlik doğrulama verisi:</strong> Oturum belirteci (session token), cihazınızda güvenli depolama alanında (iOS Keychain / Secure Store) saklanır.</Bullet>
                <Bullet><strong>Sınırlı teknik veri:</strong> Giriş güvenliği (kötüye kullanım/brute-force önleme) ve hizmetin çalışması için IP adresi geçici olarak işlenir ve standart sunucu günlükleri tutulur.</Bullet>
              </ul>
              <p className="pt-2">
                <strong>Toplamadığımız veriler:</strong> Konum verisi, reklam/izleme tanımlayıcıları, analitik veya kullanım
                takibi, çerez tabanlı reklam takibi toplanmaz. Uygulama anlık bildirim (push) göndermez ve bunun için
                cihaz jetonu toplamaz. Uygulama içinde üçüncü taraf analitik veya çökme raporlama (crash reporting) SDK’sı
                kullanılmaz.
              </p>
            </Section>

            <Section title="Verileri Nasıl Kullanıyoruz">
              <ul className="space-y-2">
                <Bullet>Hesabınızı doğrulamak ve güvenli giriş sağlamak.</Bullet>
                <Bullet>Aidat, borç, bakiye ve ödeme bilgilerinizi size göstermek.</Bullet>
                <Bullet>Malik/kiracı ayrımına göre yalnızca sizi ilgilendiren borçları göstermek.</Bullet>
                <Bullet>Yönetim duyurularını iletmek.</Bullet>
                <Bullet>Şifre sıfırlama için e-posta ile doğrulama kodu göndermek.</Bullet>
                <Bullet>Hizmeti korumak, kötüye kullanımı önlemek ve teknik sorunları gidermek.</Bullet>
              </ul>
              <p className="pt-2">Verileriniz reklam veya pazarlama amacıyla kullanılmaz ve üçüncü taraflara satılmaz.</p>
            </Section>

            <Section title="Üçüncü Taraf Hizmet Sağlayıcılar">
              <p>Hizmeti sunmak için aşağıdaki altyapı sağlayıcılarını (veri işleyenleri) kullanırız:</p>
              <ul className="space-y-2">
                <Bullet><strong>MongoDB Atlas:</strong> Uygulama verilerinin barındırılması ve saklanması.</Bullet>
                <Bullet><strong>Resend:</strong> Yalnızca şifre sıfırlama doğrulama e-postalarının gönderilmesi (e-posta adresiniz işlenir).</Bullet>
                <Bullet><strong>Uygulama barındırma sağlayıcısı:</strong> Sunucuların çalıştırılması ve güvenli bağlantı (HTTPS).</Bullet>
              </ul>
              <p className="pt-2">Bu sağlayıcılar verileri yalnızca bize hizmet sunmak amacıyla işler.</p>
            </Section>

            <Section title="Veri Saklama">
              <p>
                Hesap, daire ve finansal kayıtlar, hesabınız aktif olduğu ve apartman yönetimi tarafından ihtiyaç
                duyulduğu sürece saklanır. Hesabınızın silinmesini talep etmeniz halinde, yasal yükümlülükler saklı
                kalmak kaydıyla kişisel verileriniz makul bir süre içinde silinir veya anonim hale getirilir.
              </p>
            </Section>

            <Section title="Veri Güvenliği">
              <ul className="space-y-2">
                <Bullet>Şifreler geri döndürülemez şekilde (bcrypt) özetlenerek saklanır; açık metin olarak tutulmaz.</Bullet>
                <Bullet>Tüm veri iletişimi şifreli bağlantı (HTTPS) üzerinden yapılır.</Bullet>
                <Bullet>Oturum belirteci cihazınızda güvenli depolama alanında saklanır.</Bullet>
                <Bullet>Şifre değişikliğinde mevcut oturumlar geçersiz kılınır ve giriş denemelerine karşı hız sınırlaması uygulanır.</Bullet>
              </ul>
            </Section>

            <Section title="Haklarınız">
              <p>
                Yürürlükteki mevzuat (ör. KVKK ve GDPR) kapsamında; verilerinize erişme, düzeltilmesini isteme,
                silinmesini talep etme ve işlemeye itiraz etme haklarına sahipsiniz. Daire ve malik/kiracı bilgileri
                apartman yönetimi tarafından yönetildiğinden, bu tür düzeltmeler için yöneticinizle veya aşağıdaki
                iletişim adresi üzerinden bizimle iletişime geçebilirsiniz.
              </p>
            </Section>

            <Section title="İletişim">
              <p>
                Gizlilik politikası ve kişisel verilerinizle ilgili talepleriniz için:
              </p>
              <p>
                <span className="font-semibold">E-posta: </span>
                <a href={`mailto:${PRIVACY_EMAIL}`} data-testid="privacy-email" className="text-teal-700 font-semibold hover:text-teal-800 underline">
                  {PRIVACY_EMAIL}
                </a>
              </p>
            </Section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full border-t border-gray-200/70 bg-white/60 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-6 text-center text-sm text-gray-500" style={{ fontFamily: 'Inter, sans-serif' }}>
          <div className="mb-1">
            PandaProp ·{' '}
            <a href={`mailto:${PRIVACY_EMAIL}`} className="text-teal-700 hover:text-teal-800 underline">{PRIVACY_EMAIL}</a>
          </div>
          © {new Date().getFullYear()} PandaProp
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
